import { useModal } from '@sparklayer-io/spark-dash-ui/hooks/use-modal';
import { Badge, Button, Menu, Page, Table, Text, usePlatformTheme } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { ErrorCard } from '../../../components/error-card';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { SetupBanner } from '../../onboarding';
import { AssignPriceListModal } from '../components/modals/assign-price-list-modal';
import { ClearPriceListModal } from '../components/modals/clear-price-list-modal';
import { DeletePriceListModal } from '../components/modals/delete-price-list-modal';
import { UpgradePlanModal } from '../components/modals/upgrade-plan-modal';
import { UploadCsvModal } from '../components/modals/upload-csv-modal';
import { PriceListEmptyState } from '../components/price-list-empty-state';
import { PriceListsResourcesCard } from '../components/price-lists-resources-card';
import { PriceListTools } from '../components/price-lists-tools';
import { useExportPriceListCsv } from '../hooks/use-export-price-list-csv';
import { usePaginatedPriceLists } from '../hooks/use-price-lists';
import { PRICE_LIST_SOURCES, type PriceList } from '../types';

export const PriceListsPage = withPageProviders(PriceListsPageContent);

function PriceListsPageContent() {
  const theme = usePlatformTheme();
  const { navigationHandler } = useHandlers();
  const env = useEnv();
  const exportCsv = useExportPriceListCsv();

  const {
    pagination,
    page,
    data: priceLists,
    isLoading: isLoadingPriceLists,
    isError: isPriceListsError,
    refetch: refetchPriceLists,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = usePaginatedPriceLists([PRICE_LIST_SOURCES.CUSTOM, PRICE_LIST_SOURCES.INTEGRATION]);

  const customPriceListCount = priceLists.filter((pl) => pl.source === 'custom').length;
  const maxPriceLists = env.billing.max_price_lists;
  const hasReachedLimit =
    maxPriceLists !== undefined && maxPriceLists !== null && customPriceListCount >= maxPriceLists;

  // NOTE: don't reset selected list to null when modal is closed as then it shows empty when animating out
  const [selectedList, setSelectedList] = useState<PriceList | null>(null);
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const {
    isOpen: isClearModalOpen,
    openModal: openClearModal,
    closeModal: closeClearModal,
  } = useModal();

  const {
    isOpen: isUploadModalOpen,
    openModal: openUploadModal,
    closeModal: closeUploadModal,
  } = useModal();

  const {
    isOpen: isUpgradeModalOpen,
    openModal: openUpgradeModal,
    closeModal: closeUpgradeModal,
  } = useModal();

  return (
    <Page
      title={t('price_lists.header.title')}
      primaryAction={{
        onClick: hasReachedLimit
          ? openUpgradeModal
          : () => navigationHandler.navigate('/pricing/lists/create'),
        label: t('price_lists.header.actions.create'),
        icon: hasReachedLimit ? 'lock' : undefined,
      }}
      secondaryActions={[
        {
          onClick: () => {
            // Clear out selected list - upload modal becomes bulk if no selected list
            setSelectedList(null);
            openUploadModal();
          },
          label: t('price_lists.header.actions.bulk_upload'),
          hideOnMobile: true,
        },
      ]}
      description={t('price_lists.header.description')}
      learnMoreUrl="https://docs.sparklayer.io/price-lists"
      footerDescription={t('price_lists.footer.description')}
    >
      <SetupBanner showLaunchPrompt={false} />
      {isPriceListsError ? (
        <ErrorCard onReload={() => void refetchPriceLists()} />
      ) : !isLoadingPriceLists && priceLists.length === 0 ? (
        <PriceListEmptyState />
      ) : (
        <Table
          headings={[
            t('price_lists.table.headings.name'),
            t('price_lists.table.headings.currency'),
            t('price_lists.table.headings.type'),
            '',
          ]}
          caption={t('price_lists.table.caption')}
          loading={isLoadingPriceLists}
          pagination={{
            counts: {
              first: (page - 1) * (pagination?.per_page ?? 0) + 1,
              last: Math.min(page * (pagination?.per_page ?? 0), pagination?.total ?? 0),
              total: pagination?.total,
            },
            hasNext: !isLoadingPriceLists && hasNextPage,
            hasPrevious: !isLoadingPriceLists && hasPreviousPage,
            onNext: nextPage,
            onPrevious: previousPage,
          }}
        >
          {priceLists.map((priceList, i) => (
            <Table.Row key={priceList.slug} id={priceList.slug} index={i}>
              <Table.Cell>
                <div className="flex flex-col">
                  <Text>{priceList.name}</Text>
                  <Text size="sm" variant="secondary">
                    {t('price_lists.table.row.handle', { handle: priceList.slug })}
                  </Text>
                </div>
              </Table.Cell>
              <Table.Cell>
                <Badge>{priceList.currency_code}</Badge>
              </Table.Cell>
              <Table.Cell>
                {(priceList.rules.length === 0 || priceList.num_manual_prices > 0) && (
                  <Badge icon="upload" variant="blue">
                    {t('badges:manual', { number: priceList.num_manual_prices })}
                  </Badge>
                )}
                {priceList.rules.length > 0 && (
                  <Badge icon="refresh" variant="purple">
                    {t('badges:automatic')}
                  </Badge>
                )}
              </Table.Cell>
              <Table.Cell sticky>
                <Button
                  collapsible
                  variant="secondary"
                  onClick={() => {
                    setSelectedList(priceList);
                    openUploadModal();
                  }}
                >
                  {t('buttons:upload')}
                </Button>
                <Menu
                  collapsible
                  items={[
                    {
                      onClick: () => exportCsv(priceList.slug),
                      label: t('buttons:download_csv'),
                      icon: 'download',
                      disabled: priceList.num_manual_prices === 0,
                    },
                    {
                      onClick: () =>
                        navigationHandler.navigate(
                          `/pricing/lists/edit/${encodeURIComponent(priceList.slug)}`,
                        ),
                      label: t('buttons:edit'),
                      icon: 'edit',
                    },
                    {
                      onClick: () => {
                        setSelectedList(priceList);
                        openClearModal();
                      },
                      label: t('buttons:clear'),
                      icon: 'minusCircle',
                    },
                    {
                      onClick: () => {
                        setSelectedList(priceList);
                        openDeleteModal();
                      },
                      label: t('buttons:delete'),
                      icon: 'delete',
                      danger: true,
                    },
                  ]}
                  buttonText={t('buttons:more_actions')}
                  align="end"
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      )}

      {/*  Remove direct links into the dashboard from Shopify on primary workflows */}
      {theme !== 'polaris' && (
        <>
          <PriceListTools />
          <PriceListsResourcesCard />
        </>
      )}

      <DeletePriceListModal
        priceList={selectedList}
        isOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
      />

      <ClearPriceListModal
        priceList={selectedList}
        isOpen={isClearModalOpen}
        closeModal={closeClearModal}
      />

      <UploadCsvModal
        priceList={selectedList}
        isOpen={isUploadModalOpen}
        closeModal={closeUploadModal}
      />

      <UpgradePlanModal isOpen={isUpgradeModalOpen} closeModal={closeUpgradeModal} />

      <AssignPriceListModal />
    </Page>
  );
}
